import { Box, Typography, Skeleton } from '@mui/material';
import { LineChart } from '@mui/x-charts/LineChart';

export interface DashboardLineChartProps {
  displayName: string;
  data: Record<string, unknown>[];
  dimension: string;
  measures: Array<{
    key: string;
    label: string;
    color?: string;
    yAxis?: 'left' | 'right';
  }>;
  loading?: boolean;
}

export function DashboardLineChart({
  displayName,
  data,
  dimension,
  measures,
  loading,
}: DashboardLineChartProps) {
  const safeData = Array.isArray(data)
    ? data.map((row) => {
        const cleaned: Record<string, unknown> = {};
        for (const [k, v] of Object.entries(row)) {
          cleaned[k] = v ?? 0;
        }
        return cleaned;
      })
    : [];

  if (loading || safeData.length === 0) {
    return (
      <Box sx={{ p: 2, height: '100%' }}>
        <Skeleton width="40%" height={24} />
        <Skeleton variant="rectangular" height="calc(100% - 40px)" sx={{ mt: 1, borderRadius: 1 }} />
      </Box>
    );
  }

  const hasRightAxis = measures.some((m) => m.yAxis === 'right');

  const yAxes = [
    { id: 'left', position: 'left' as const },
    ...(hasRightAxis ? [{ id: 'right', position: 'right' as const }] : []),
  ];

  const series = measures.map((m) => ({
    dataKey: m.key,
    label: m.label,
    color: m.color,
    yAxisId: m.yAxis === 'right' ? 'right' : 'left',
  }));

  return (
    <Box sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
        {displayName}
      </Typography>
      <Box sx={{ flexGrow: 1, minHeight: 0 }}>
        <LineChart
          dataset={safeData}
          xAxis={[{ dataKey: dimension, scaleType: 'point' }]}
          yAxis={yAxes}
          series={series}
          slotProps={{ tooltip: { trigger: 'item' } }}
          height={300}
          margin={{ top: 20, right: hasRightAxis ? 60 : 20, bottom: 30, left: 60 }}
        />
      </Box>
    </Box>
  );
}
